import { createAsyncThunk } from '@reduxjs/toolkit';

import { ApiPaths } from '../../../api/APIPaths';
import APIDataProvider, { TApiResponse } from '../../../api/ApiDataProvider';
import { ICourse } from '../CoursesSlice';
import { ISection } from '.';
import { THomeworkFile, THomeworkQuiz } from '../HomeworksSlice';

const apiDataProvider = new APIDataProvider();

export type TCancelCourseZoomSectionAsUser = {
  _id: string;
  reason: string;
};

export type TRescheduleCourseZoomSectionAsUser = {
  sectionId: string;
  reason: string;
  start: string;
  end: string;
  duration: number;
  title: string;
  isReschduleFreeSlot?: boolean;
};

export type TRescheduleCourseTeamsSectionAsUser = {
  sectionId: string;
  reason: string;
  start: string;
  end: string;
  duration: number;
  title: string;
  isReschduleFreeSlot?: boolean;
};

export type TCreateCourseHomeworkSectionAsUser = {
  parent?: string;
  sortingKey: number;
  course: string;
  title: string;
  dueDate: string;
  description: string;
  link?: string;
  files?: Array<THomeworkFile>;
  quizzes?: Array<THomeworkQuiz>;
};

export type TUpdateCourseHomeworkSectionAsUser = {
  _id: string;
  title: string;
  dueDate: string;
  description: string;
};

export type TCancelCourseTeamsSectionAsUser = {
  _id: string;
  reason: string;
};

export type TBookCourseMeeting = {
  start: string;
  tutorId: string;
  courseId: string;
};

export const cancelCourseTeamsSectionAsUser = createAsyncThunk(
  '/sections/cancelOneTeam',
  async (data: TCancelCourseTeamsSectionAsUser) => {
    const path = `${ApiPaths.teamMeetings}/cancel/${data._id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const rescheduleCourseTeamsSectionAsUser = createAsyncThunk(
  '/sections/rescheduleOneTeam',
  async (data: TRescheduleCourseTeamsSectionAsUser) => {
    const path = `${ApiPaths.teamMeetings}/reschedule/${data.sectionId}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const getUserCourseSectionsByCourseId = createAsyncThunk(
  '/courses/id/sections',
  async (id: string) => {
    const path = `${ApiPaths.courses}/${id}?subSections=1`;
    const response = await apiDataProvider.get<TApiResponse<ICourse>>(path);

    return response.data;
  }
);

export const cancelCoursZoomeSectionAsUser = createAsyncThunk(
  '/sections/cancelOneZoom',
  async (data: TCancelCourseZoomSectionAsUser) => {
    const path = `${ApiPaths.zoomMeetings}/cancel/${data._id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const rescheduleCoursZoomeSectionAsUser = createAsyncThunk(
  '/sections/rescheduleOneZoom',
  async (data: TRescheduleCourseZoomSectionAsUser) => {
    const path = `${ApiPaths.zoomMeetings}/reschedule/${data.sectionId}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const getSectionByIdAsUser = createAsyncThunk(
  '/sections/getOne',
  async (data: { id: string }) => {
    const path = `${ApiPaths.sections}/${data.id}`;
    const response = await apiDataProvider.get<TApiResponse<ISection>>(path);

    return response.data;
  }
);

export const createCourseHomeworkSectionAsUser = createAsyncThunk(
  '/sections/createHomework',
  async (data: TCreateCourseHomeworkSectionAsUser) => {
    const path = ApiPaths.homeworks;
    const response = await apiDataProvider.post<
      TApiResponse<{ section: ISection; parentSection: ISection }>
    >(path, data);

    return response.data;
  }
);

export const updateCourseHomeworkSectionAsUser = createAsyncThunk(
  '/sections/updateHomework',
  async ({ _id, ...data }: TUpdateCourseHomeworkSectionAsUser) => {
    const path = `${ApiPaths.homeworks}/${_id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const bookCourseMeeting = createAsyncThunk(
  '/team-meetings/course-meeting-book',
  async (data: TBookCourseMeeting) => {
    const path = `${ApiPaths.teamMeetings}/course-meeting-book`;
    const response = await apiDataProvider.post<TApiResponse<ISection[]>>(
      path,
      data
    );

    return response.data;
  }
);
